/**
 * LeadSlideOver — right-side preview panel rendered 1:1 from
 *   /Users/gudupuramesh/Documents/design ui/anti Interior io ui web/leads.html
 *   (the `<div class="slide-over" id="details-panel">` element +
 *    the `app.js → openDetails('lead')` template).
 *
 * Real data: `useLead(leadId)` live snapshot. The panel renders the
 * exact same `.slide-over` / `.slide-header` / `.detail-section` /
 * `.detail-row` / `.timeline` markup the folder uses, populated from
 * the lead document.
 *
 * Quick actions:
 *   - `Call` (primary, opens tel:)
 *   - `Email` (secondary, opens mailto:)
 *
 * Header:
 *   - `Edit` button → navigates to `/leads/{id}/edit` (full-page form)
 *   - `×` close button
 *
 * Controlled: `leadId` drives the open/close state. When `leadId` is
 * a string we fade the overlay in and slide the panel in from the
 * right; when `null`, we slide out.
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  budgetRangeLabel,
  leadSourceLabel,
  leadStatusLabel,
  possessionStatusLabel,
  projectTypeLabel,
  useLead,
  type LeadListItem,
  type LeadPriority,
  type LeadStatus,
} from '@/lib/useLeads';

export type LeadSlideOverProps = {
  leadId: string | null;
  onClose: () => void;
};

// ───── Helpers ─────

function stagePillClass(s: LeadStatus): string {
  switch (s) {
    case 'new':                  return 'stage-discovery';
    case 'contacted':            return 'stage-approval';
    case 'site_visit_scheduled': return 'stage-design';
    case 'proposal_sent':        return 'stage-execution';
    case 'negotiation':          return 'stage-handover';
    case 'converted':            return 'stage-completed';
    case 'lost':                 return 'stage-completed';
  }
}

function priorityColor(p: LeadPriority): string | undefined {
  if (p === 'hot')    return 'var(--brand)';
  if (p === 'high')   return 'var(--brand)';
  if (p === 'medium') return 'var(--warning)';
  if (p === 'low')    return 'var(--text-secondary)';
  return undefined;
}

function priorityLabel(p: LeadPriority): string {
  return p[0].toUpperCase() + p.slice(1);
}

function formatDate(ms: number | null | undefined): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatDateTime(ms: number | null | undefined): string {
  if (!ms) return '—';
  const d = new Date(ms);
  return `${d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}, ${d.toLocaleTimeString(
    'en-IN',
    { hour: '2-digit', minute: '2-digit' },
  )}`;
}

function compactInr(n: number | undefined | null): string {
  if (!n || n <= 0) return '—';
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
  if (n >= 100000)   return `₹${Math.round(n / 100000)}L`;
  if (n >= 1000)     return `₹${Math.round(n / 1000)}K`;
  return `₹${n.toLocaleString('en-IN')}`;
}

function leadValueText(l: LeadListItem): string {
  if (l.budgetRange) return budgetRangeLabel(l.budgetRange);
  return compactInr(l.budget);
}

function subtitleOf(l: LeadListItem): string {
  const parts: string[] = [];
  if (l.projectType) parts.push(projectTypeLabel(l.projectType));
  if (l.location) parts.push(l.location);
  return parts.length ? parts.join(' · ') : l.phone || '—';
}

// ───── Component ─────

export function LeadSlideOver({ leadId, onClose }: LeadSlideOverProps) {
  const navigate = useNavigate();
  const open = leadId !== null;
  const { lead, loading, error } = useLead(leadId ?? undefined);

  // Lock body scroll while the panel is open (mirrors typical
  // slide-over UX; iio.css doesn't manage this on its own).
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Close on Esc.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return (
    <>
      <div
        className={`slide-overlay${open ? ' open' : ''}`}
        onClick={onClose}
        aria-hidden
      />
      <div
        className={`slide-over${open ? ' open' : ''}`}
        role="dialog"
        aria-label="Lead details"
        aria-hidden={!open}
      >
        <div className="slide-header">
          <h2 className="slide-title">Lead Details</h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {lead ? (
              <button
                className="btn-secondary"
                type="button"
                style={{ height: 32, padding: '0 12px', fontSize: 12 }}
                onClick={() => navigate(`/leads/${lead.id}/edit`)}
              >
                <i className="ph ph-pencil-simple" /> Edit
              </button>
            ) : null}
            <button
              className="slide-close"
              type="button"
              onClick={onClose}
              aria-label="Close"
            >
              <i className="ph ph-x" />
            </button>
          </div>
        </div>

        <div className="slide-body">
          {!open ? null : loading ? (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-secondary)' }}>
              Loading…
            </div>
          ) : error ? (
            <div
              className="card"
              style={{
                padding: 12,
                background: 'var(--critical-bg)',
                color: 'var(--critical-text)',
                borderColor: 'var(--critical)',
              }}
            >
              <i className="ph ph-warning-circle" /> {error}
            </div>
          ) : !lead ? (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-secondary)' }}>
              Lead not found.
            </div>
          ) : (
            <>
              {/* Client */}
              <div className="detail-section">
                <div className="detail-label">Client</div>
                <div className="detail-text" style={{ fontWeight: 600, fontSize: 18 }}>
                  {lead.name}
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                  {subtitleOf(lead)}
                </div>
              </div>

              {/* Quick actions */}
              <div className="detail-section" style={{ flexDirection: 'row', gap: 16 }}>
                {lead.phone ? (
                  <a
                    className="btn-primary"
                    href={`tel:${lead.phone}`}
                    style={{ flex: 1, justifyContent: 'center', textDecoration: 'none' }}
                  >
                    <i className="ph ph-phone" /> {lead.phone}
                  </a>
                ) : (
                  <button
                    className="btn-primary"
                    type="button"
                    style={{ flex: 1, justifyContent: 'center', opacity: 0.5 }}
                    disabled
                  >
                    <i className="ph ph-phone" /> No phone
                  </button>
                )}
                {lead.email ? (
                  <a
                    className="btn-secondary"
                    href={`mailto:${lead.email}`}
                    style={{ flex: 1, justifyContent: 'center', textDecoration: 'none' }}
                  >
                    <i className="ph ph-envelope" /> Email
                  </a>
                ) : (
                  <button
                    className="btn-secondary"
                    type="button"
                    style={{ flex: 1, justifyContent: 'center', opacity: 0.5 }}
                    disabled
                  >
                    <i className="ph ph-envelope" /> No email
                  </button>
                )}
              </div>

              {/* Card 1 — Project & pipeline */}
              <div className="card" style={{ padding: 16, marginTop: 8 }}>
                <div className="detail-row">
                  <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                    Estimated Value
                  </span>
                  <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                    {leadValueText(lead)}
                  </span>
                </div>
                <div className="detail-row">
                  <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                    Possession
                  </span>
                  <span style={{ fontWeight: 500 }}>
                    {lead.possessionStatus
                      ? possessionStatusLabel(lead.possessionStatus)
                      : '—'}
                  </span>
                </div>
                <div className="detail-row">
                  <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                    Source
                  </span>
                  <span>{leadSourceLabel(lead.source)}</span>
                </div>
                <div className="detail-row">
                  <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                    Status
                  </span>
                  <span className={`stage-pill ${stagePillClass(lead.status)}`}>
                    {leadStatusLabel(lead.status)}
                  </span>
                </div>
              </div>

              {/* Card 2 — Management */}
              <div className="card" style={{ padding: 16, marginTop: 16 }}>
                <div className="detail-row">
                  <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                    Assigned To
                  </span>
                  <span style={{ fontWeight: 500 }}>
                    {lead.assignedTo?.trim() || 'Unassigned'}
                  </span>
                </div>
                <div className="detail-row">
                  <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                    Priority
                  </span>
                  <span
                    style={{
                      fontWeight: 500,
                      color: priorityColor(lead.priority),
                    }}
                  >
                    {priorityLabel(lead.priority)}
                  </span>
                </div>
                <div className="detail-row">
                  <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                    Follow-up Date
                  </span>
                  <span>{formatDate(lead.followUpAt?.toMillis() ?? null)}</span>
                </div>
              </div>

              {/* Notes */}
              {lead.notes?.trim() ? (
                <div className="detail-section" style={{ marginTop: 16 }}>
                  <div className="detail-label">Initial Notes</div>
                  <div
                    style={{
                      fontSize: 13,
                      color: 'var(--text-primary)',
                      background: '#FAFAFA',
                      padding: 12,
                      borderRadius: 6,
                      border: '1px solid var(--border)',
                      lineHeight: 1.5,
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {lead.notes}
                  </div>
                </div>
              ) : null}

              {/* Activity */}
              <div className="detail-section">
                <div className="detail-label">Activity</div>
                <div className="timeline">
                  <div className="timeline-item">
                    <div className="timeline-dot" style={{ background: 'var(--brand)' }} />
                    <div className="timeline-content">
                      <div className="timeline-title">
                        Lead created via {leadSourceLabel(lead.source)}
                      </div>
                      <div className="timeline-time">
                        {formatDateTime(lead.createdAt?.toMillis() ?? null)}
                      </div>
                    </div>
                  </div>
                  {lead.followUpAt ? (
                    <div className="timeline-item">
                      <div className="timeline-dot" />
                      <div className="timeline-content">
                        <div className="timeline-title">
                          Follow-up scheduled
                        </div>
                        <div className="timeline-time">
                          {formatDate(lead.followUpAt.toMillis())}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
